import React from 'react';
import { MenuIdProjects } from '@jira/navigation-apps__constants/src/MenuIdProjects.tsx';
// eslint-disable-next-line jira/import/no-restricted-import
import { getBusinessProjectResources } from '@jira/router-resources__business-common/src/getBusinessProjectResources.tsx';
import type RelayProjectVersionsType from '@jira/spa-apps__project-relay-versions/src/ExportedDefault.tsx';
import { getNavigationResources } from '@jira/router-resources__navigation/src/getNavigationResources.tsx';
import { projectContextResource } from '@jira/router-resources__project-context/src/projectContextResource.tsx';
import { projectVersionsRelayResource } from '@jira/router-resources__project-versions/src/projectVersionsRelayResource.tsx';
import { incidentExperimentTraitResource } from '@jira/router-resources__software-incidents/src/incidentExperimentTraitResource.tsx';
import { LazyAtlassianNavigation } from '@jira/navigation-apps__atlassian-navigation/src/async.tsx';
import { genericProjectLayout } from '@jira/platform__layouts-generic-project-layout/src/index.tsx';
import { GENERIC_PROJECT_HORIZONTAL_NAV } from '@jira/navigation-apps__horizontal-nav-selector/src/constants.tsx';
import { forgeProjectModuleResource } from '@jira/navigation-apps__resources/src/controllers/forge/index.tsx';
import { horizontalNavJiraResource } from '@jira/navigation-apps__resources/src/controllers/horizontal-navigation/horizontal-nav-jira/index.tsx';
import { staticProjectSidebarConnectAddonsNavigationResource } from '@jira/navigation-apps__resources/src/controllers/sidebar/static-project-sidebar-connect-addons/index.tsx';
import { staticProjectSidebarNavigationResource } from '@jira/navigation-apps__resources/src/controllers/sidebar/static-project-sidebar/index.tsx';
import { createEntry } from '@jira/platform__routing-route-entry/src/createEntry.tsx';
import { projectVersionsRoute } from '@jira/router-routes__projects-versions-routes/src/projectVersionsRoute.tsx';
import { markAsProductBundleReady } from '@jira/platform__spa/src/services/mark-as-product-bundle-ready/index.tsx';
import { LazyPage } from '@jira/platform__spa/src/view/lazy-page/index.tsx';
import { lazyForPaint } from '@atlassian/react-loosely-lazy';
import type { Route } from '@atlassian/react-resource-router';

const LazyRelayProjectVersions = lazyForPaint<typeof RelayProjectVersionsType>(() =>
	markAsProductBundleReady(
		import(
			/* webpackChunkName: "async-relay-project-versions", jiraSpaEntry: "async-relay-project-versions" */ '@jira/spa-apps__project-relay-versions/src/ExportedDefault.tsx'
		),
	),
);

const RelayVersions = () => (
	<LazyPage
		Page={LazyRelayProjectVersions}
		pageId="relay-project-versions"
		shouldShowSpinner={false}
	/>
);

export const projectVersionsRouteEntry: Route = createEntry(projectVersionsRoute, () => ({
	group: 'components',

	resources: [
		...getBusinessProjectResources(),
		...getNavigationResources(),
		// these have been expanded from getNavigationSidebarProjectResource - please examine whether your route requires these resources in nav4 and remove them if needed
		// A guide to check & cleanup: https://hello.atlassian.net/wiki/x/Q8XybwE
		incidentExperimentTraitResource,
		// eslint-disable-next-line jira/router/no-project-context-resource
		projectContextResource,
		forgeProjectModuleResource,
		staticProjectSidebarNavigationResource,
		staticProjectSidebarConnectAddonsNavigationResource,
		horizontalNavJiraResource,
		// end of resources expanded from getNavigationSidebarProjectResource
		projectVersionsRelayResource,
	],

	// eslint-disable-next-line no-restricted-syntax
	component: RelayVersions,
	layout: genericProjectLayout,

	navigation: {
		menuId: MenuIdProjects,
		horizontal: GENERIC_PROJECT_HORIZONTAL_NAV,
	},

	forPaint: [LazyAtlassianNavigation, LazyRelayProjectVersions],
	supportsPreviewPanel: true,

	bifrost: {
		bifrostGroup: 'shell-only',
	},
}));
